import React, { useEffect, useMemo, useState } from 'react';
import ContentModal from '../../components/modals/ContentModal';
import { contentApiService } from '../../api/contentApi';
import { usersAPI } from '../../api/api';

const MONTHS = [
  { name: 'OCAK - JANUARY', value: 0 },
  { name: 'ŞUBAT - FEBRUARY', value: 1 },
  { name: 'MART - MARCH', value: 2 },
  { name: 'NİSAN - APRIL', value: 3 },
  { name: 'MAYIS - MAY', value: 4 },
  { name: 'HAZİRAN - JUNE', value: 5 },
  { name: 'TEMMUZ - JULY', value: 6 },
  { name: 'AĞUSTOS - AUGUST', value: 7 },
  { name: 'EYLÜL - SEPTEMBER', value: 8 },
  { name: 'EKİM - OCTOBER', value: 9 },
  { name: 'KASIM - NOVEMBER', value: 10 },
  { name: 'ARALIK - DECEMBER', value: 11 },
];

const CONTENT_TYPE_COLORS = {
  story: 'bg-purple-500',
  post: 'bg-blue-500',
  reels: 'bg-pink-500',
  shorts: 'bg-red-500',
  video: 'bg-green-500',
  carousel: 'bg-yellow-500',
};

const ContentCalendar = () => {
  const [year] = useState(2026);
  const [contents, setContents] = useState([]);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const referenceData = useMemo(
    () => ({
      channels,
      users,
    }),
    [channels, users]
  );

  const loadAll = async () => {
    try {
      setLoading(true);
      const [contentsResponse, channelsResponse, usersResponse] = await Promise.all([
        contentApiService.getPlans(),
        contentApiService.getChannels(),
        usersAPI.getUsers(),
      ]);

      setContents(contentsResponse?.success ? contentsResponse.data || [] : []);
      setChannels(channelsResponse?.success ? channelsResponse.data || [] : []);
      setUsers(
        usersResponse?.success
          ? (usersResponse.data || []).map((user) => ({
              ...user,
              name: user.full_name || user.email,
            }))
          : []
      );
    } catch (err) {
      // Silent for now
    } finally {
      setLoading(false);
    }
  };

  const isWeekend = (day, monthIndex) => {
    const date = new Date(year, monthIndex, day);
    const d = date.getDay();
    return d === 0 || d === 6;
  };

  const getDaysInMonth = (monthIndex) => {
    const date = new Date(year, monthIndex + 1, 0);
    const days = [];
    for (let i = 1; i <= date.getDate(); i += 1) {
      days.push(i);
    }
    return days;
  };

  const getFirstDayOfWeek = (targetYear, monthIndex) => new Date(targetYear, monthIndex, 1).getDay();

  const getRowCells = (contentName, monthIndex, daysInMonth) => {
    const cells = [];
    const events = contents.filter((content) => content.content_name === contentName);
    const firstDay = getFirstDayOfWeek(year, monthIndex);

    for (let i = 0; i < firstDay; i += 1) {
      cells.push({ day: null, events: [] });
    }

    for (let day = 1; day <= daysInMonth.length; day += 1) {
      const currentDt = new Date(year, monthIndex, day);
      const checkDt = new Date(currentDt.getFullYear(), currentDt.getMonth(), currentDt.getDate());

      const eventsForDay = events.filter((event) => {
        if (!event.share_date) return false;
        const eventDate = new Date(event.share_date);
        const normalized = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
        return checkDt.getTime() === normalized.getTime();
      });

      cells.push({ day, events: eventsForDay });
    }
    return cells;
  };

  const handleEventClick = (event, e) => {
    e.stopPropagation();
    setSelectedContent(event);
    setIsModalOpen(true);
  };

  const handleSaveContent = async (data, selectedFiles = [], selectedPhotoGroup = 'post_grid') => {
    try {
      let response = null;
      if (data.id) {
        response = await contentApiService.updatePlan(data.id, data);
      } else {
        response = await contentApiService.createPlan(data);
      }
      const savedPlanId = response?.data?.id || data.id;
      if (savedPlanId && selectedFiles.length > 0) {
        await contentApiService.uploadPlanPhotos(savedPlanId, selectedFiles, selectedPhotoGroup);
      }
      setIsModalOpen(false);
      setSelectedContent(null);
      loadAll();
    } catch (err) {
      // Silent for now
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await contentApiService.deletePlanPhoto(photoId);
      await loadAll();
      if (selectedContent?.id) {
        const refreshed = await contentApiService.getPlans();
        if (refreshed?.success) {
          const found = (refreshed.data || []).find((item) => item.id === selectedContent.id);
          setSelectedContent(found || null);
        }
      }
    } catch (err) {
      // Silent for now
    }
  };

  const uniqueContentNames = [...new Set(contents.map((item) => item.content_name))].filter(Boolean);

  return (
    <div className="px-3 py-4 sm:px-4 md:p-6 bg-white min-h-screen overflow-x-auto dark:bg-gray-900 select-none">
      <ContentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveContent}
        onDeletePhoto={handleDeletePhoto}
        initialData={selectedContent}
        referenceData={referenceData}
      />

      <h1 className="text-2xl font-bold text-center mb-4 text-gray-900 dark:text-white uppercase tracking-wider">
        CONTENT CALENDAR {year}
      </h1>

      <div className="flex flex-wrap justify-start md:justify-end gap-3 md:gap-6 text-xs font-bold mb-4">
        {Object.entries(CONTENT_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-2">
            <div className={`w-4 h-4 ${color} rounded`} />
            <span className="text-gray-700 dark:text-gray-300 uppercase">{type}</span>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-10">
        {MONTHS.map((month) => {
          const days = getDaysInMonth(month.value);
          if (uniqueContentNames.length === 0) return null;

          return (
            <div key={month.value} className="border-2 border-gray-800 dark:border-gray-500 shadow-sm">
              <div className="bg-[#9bc2e6] text-black font-extrabold text-center py-1.5 border-b-2 border-gray-800 text-sm tracking-wide">
                {month.name}
              </div>

              <table className="w-full border-collapse table-fixed text-[11px]">
                <colgroup>
                  <col className="w-48 min-w-[12rem]" />
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => <col key={day} />)}
                </colgroup>
                <thead>
                  <tr>
                    <th className="border border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-48 min-w-[12rem] sticky left-0 z-20" />
                    {getRowCells(uniqueContentNames[0] || '', month.value, days).map((cell, idx) => {
                      if (cell.day === null) {
                        return <th key={`empty-${idx}`} className="border border-gray-600 bg-white dark:bg-gray-800" />;
                      }
                      const isWknd = isWeekend(cell.day, month.value);
                      return (
                        <th
                          key={cell.day}
                          className={`border border-gray-600 border-b-2 h-8 text-center ${isWknd ? 'bg-red-600 text-white' : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100'}`}
                        >
                          {cell.day}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {uniqueContentNames.map((contentName) => {
                    const cells = getRowCells(contentName, month.value, days);
                    return (
                      <tr key={contentName} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        <td className="border-r-2 border-b border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 font-medium px-2 py-1 truncate sticky left-0 z-10 w-48 min-w-[12rem] text-xs h-10 align-middle">
                          {contentName}
                        </td>
                        {cells.map((cell, idx) => {
                          if (cell.day === null) {
                            return <td key={`empty-${idx}`} className="border border-gray-400 h-10 bg-white dark:bg-gray-800" />;
                          }
                          const isWknd = isWeekend(cell.day, month.value);
                          return (
                            <td
                              key={cell.day}
                              className={`border border-gray-400 h-10 align-middle p-0.5 relative group ${isWknd ? 'bg-red-100 dark:bg-red-900/30' : 'bg-white dark:bg-gray-800'}`}
                            >
                              {cell.events.length > 0 && cell.events.map((event) => (
                                <div
                                  key={event.id}
                                  onClick={(e) => handleEventClick(event, e)}
                                  className={`w-full h-full ${CONTENT_TYPE_COLORS[event.content_type] || 'bg-gray-500'} text-white text-[8px] px-0.5 py-0.5 rounded cursor-pointer hover:opacity-80 flex items-center justify-center font-semibold`}
                                  title={`${event.content_name} - ${event.content_type}`}
                                >
                                  {event.content_name?.substring(0, 4) || ''}
                                </div>
                              ))}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-white p-4 rounded-lg shadow-lg font-semibold">İşlem Yapılıyor...</div>
        </div>
      )}
    </div>
  );
};

export default ContentCalendar;

